import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import { getMediaById, mediaList } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFavorites } from '@/hooks/useFavorites';

const episodeTitles = [
  '初到边关', '雨夜的证词', '第二封信', '旧城的钟', '谁在说谎', '锈迹之下',
  '七次日落', '沉默的代价', '回程票', '天台上的晚餐', '最后一次通话', '天亮之前',
];

export default function DetailPage() {
  const { id = '' } = useParams();
  const item = getMediaById(id);
  const { isFavorite, toggle } = useFavorites();
  const [episode, setEpisode] = useState(1);
  const [copied, setCopied] = useState(false);

  usePageMeta({
    title: item ? `${item.title} | 影视详情 - 菠萝蜜网` : '影视详情 - 菠萝蜜网',
    description: item ? `${item.title}：${item.summary.slice(0, 78)}` : '菠萝蜜网影视详情页',
    keywords: item ? `${item.title},${item.genres.join(',')},${item.type}详情,影视推荐` : '影视详情',
    path: `/detail/${id}`,
    ogImage: item?.backdrop,
  });

  if (!item) {
    return (
      <SiteLayout>
        <PortalShell>
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <h1 className="font-heading text-xl font-bold text-foreground-950">作品不存在或已下架</h1>
            <p className="mt-2 text-xs text-foreground-500">可能是链接输入有误，去分类页看看其他作品吧。</p>
            <Link to="/category/电影" className="mt-4 inline-block whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50">
              浏览电影分类
            </Link>
          </div>
        </PortalShell>
      </SiteLayout>
    );
  }

  const favorite = isFavorite(item.id);
  const isSeries = typeof item.episodes === 'number' && item.episodes > 1;
  const episodeCount = item.episodes ?? 0;
  const related = mediaList
    .filter((media) => media.id !== item.id)
    .map((media) => ({
      media,
      score: media.genres.filter((genre) => item.genres.includes(genre)).length * 3 + (media.type === item.type ? 1 : 0),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.media.heat - a.media.heat)
    .slice(0, 6)
    .map((entry) => entry.media);
  const relatedNews = newsList.filter((news) => news.relatedId === item.id);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const infoRows = [
    { label: '类型', value: item.type },
    { label: '题材', value: item.genres.join(' / ') },
    { label: '导演', value: item.director },
    { label: '主演', value: item.cast.join('、') },
    { label: '上映时间', value: `${item.year} 年` },
    { label: '地区', value: item.region },
    { label: '片长', value: item.runtime },
    { label: '评分', value: `${item.rating.toFixed(1)} / 10` },
  ];

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb
          items={[
            { label: item.type, to: `/category/${encodeURIComponent(item.type)}` },
            { label: item.genres[0], to: `/list/${encodeURIComponent(item.genres[0])}` },
            { label: item.title },
          ]}
        />

        <section className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="relative h-[180px] w-full md:h-[240px]">
            <img src={item.backdrop} alt={`${item.title} 剧照`} title={`${item.title} 影视详情`} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-950/30" />
          </div>

          <div className="relative -mt-24 px-4 pb-5 md:-mt-28 md:px-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="h-[180px] w-[124px] shrink-0 overflow-hidden rounded-lg border-2 border-background-50 bg-background-200 shadow-md md:h-[210px] md:w-[148px]">
                <img src={item.poster} alt={`${item.title} 封面`} title={`${item.title} 海报`} className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0 flex-1 md:pb-1">
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{item.title}</h1>
                <p className="mt-1 text-xs text-foreground-500">{item.enTitle}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">
                    <i className="ri-star-fill w-3 h-3 flex items-center justify-center text-[10px]" />
                    {item.rating.toFixed(1)}
                  </span>
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{item.type}</span>
                  {item.genres.map((genre) => (
                    <Link key={genre} to={`/list/${encodeURIComponent(genre)}`} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-700 transition hover:bg-primary-100 hover:text-primary-800">
                      {genre}
                    </Link>
                  ))}
                </div>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.tagline}</p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => toggle(item.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition ${
                  favorite
                    ? 'bg-secondary-500 text-background-50 hover:bg-secondary-600'
                    : 'border border-secondary-300 bg-secondary-50 text-secondary-800 hover:bg-secondary-100'
                }`}
              >
                <i className={`${favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} w-4 h-4 flex items-center justify-center`} />
                {favorite ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-link" />
                {copied ? '链接已复制' : '复制链接'}
              </button>
              <div className="flex items-center gap-1.5">
                {[
                  { icon: 'ri-wechat-line', label: '微信' },
                  { icon: 'ri-weibo-line', label: '微博' },
                  { icon: 'ri-douban-line', label: '豆瓣' },
                ].map((share) => (
                  <span
                    key={share.label}
                    title={`分享到${share.label}（演示）`}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition hover:border-primary-400 hover:text-primary-700"
                  >
                    <i className={`${share.icon} text-base`} />
                  </span>
                ))}
              </div>
              <Link
                to={`/category/${encodeURIComponent(item.type)}`}
                className="ml-auto flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition hover:text-primary-700"
              >
                <i className="ri-arrow-left-line w-3.5 h-3.5 flex items-center justify-center" />
                返回{item.type}分类
              </Link>
            </div>
          </div>
        </section>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品简介</h2>
              <p className="mt-2.5 text-[13px] leading-relaxed text-foreground-700">{item.summary}</p>
              <h3 className="mt-5 font-heading text-sm font-bold text-foreground-950">剧情介绍</h3>
              <p className="mt-2 text-[13px] leading-[1.9] text-foreground-600">{item.story}</p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-foreground-950">在线播放</h2>
                <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] text-foreground-500">演示区域</span>
              </div>
              <div className="mt-3 flex h-[180px] flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/70 md:h-[220px]">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-play-circle-line text-2xl" />
                </span>
                <p className="mt-3 text-sm text-foreground-700">本站不提供在线播放与下载</p>
                <p className="mt-1 text-xs text-foreground-500">播放器为演示占位，暂无片源接入，欢迎前往正版平台观看。</p>
              </div>
            </section>

            {isSeries && (
              <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-heading text-base font-bold text-foreground-950">剧集章节</h2>
                  <span className="text-[11px] text-foreground-500">共 {episodeCount} 集 · 当前第 {episode} 集</span>
                </div>
                <div className="no-scrollbar mt-3 grid max-h-[220px] grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3 md:grid-cols-4">
                  {Array.from({ length: Math.min(episodeCount, 24) }, (_, index) => index + 1).map((number) => (
                    <button
                      key={number}
                      type="button"
                      onClick={() => setEpisode(number)}
                      className={`flex flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition ${
                        number === episode
                          ? 'border-primary-400 bg-primary-50 text-primary-800'
                          : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'
                      }`}
                    >
                      <span className="text-[11px] font-medium">第 {number} 集</span>
                      <span className="w-full truncate text-[11px] text-foreground-500">{episodeTitles[(number - 1) % episodeTitles.length]}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-3 rounded-md bg-background-100 px-3 py-2 text-[11px] text-foreground-500">
                  第 {episode} 集《{episodeTitles[(episode - 1) % episodeTitles.length]}》为演示章节信息，暂无播放源。
                </p>
              </section>
            )}

            <section>
              <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                {related.slice(0, 3).map((media, index) => (
                  <MediaCard key={media.id} id={media.id} index={index} />
                ))}
              </div>
              {related.length > 3 && (
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {related.slice(3).map((media) => (
                    <MediaCard key={media.id} id={media.id} layout="row" />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-5">
            <section className="rounded-lg border border-background-200 bg-background-50">
              <div className="border-b border-background-200 px-4 py-3">
                <h2 className="font-heading text-sm font-bold text-foreground-950">基本信息</h2>
              </div>
              <dl className="divide-y divide-background-100 px-4">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 py-2.5 text-xs">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950">标签</h2>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] text-accent-900">{tag}</span>
                ))}
              </div>
            </section>

            {relatedNews.length > 0 && (
              <section className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关资讯</h2>
                <div className="mt-2.5 space-y-2.5">
                  {relatedNews.map((news) => (
                    <div key={news.id} className="border-l-2 border-background-200 pl-2.5">
                      <p className="text-xs leading-relaxed text-foreground-700">{news.title}</p>
                      <p className="mt-1 text-[10px] text-foreground-400">{news.date} · {news.category}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-dashed border-background-300 bg-background-100/60 p-4">
              <p className="text-[11px] leading-relaxed text-foreground-500">
                本页所有作品信息、评分与剧集章节均为演示数据，仅用于展示资讯与推荐功能，不代表真实作品资料。
              </p>
            </section>
          </aside>
        </div>
      </PortalShell>
    </SiteLayout>
  );
}